/**
 * TPU Premium Hat Patches — $10 order-minimum auto-adjustment.
 *
 * DPO's per-line formula can't see the whole cart, so it can't enforce the
 * doc's rule that total patch price (before setup fees) must reach $10
 * across all TPU Hat Patches line items combined. This reconstructs that
 * check client-side and silently tops the order up to exactly $10.00 of
 * patch value using a hidden "Order Minimum Adjustment" product, built from
 * denomination variants ($5/$1/$0.25/$0.05/$0.01) so any shortfall can be
 * made up to the exact cent — the same way you'd make change. The
 * adjustment line itself is hidden from cart/mini-cart display (see
 * main-cart.liquid and mini-cart.liquid) — its cost is still included in
 * cart.total_price, it just doesn't render as its own row.
 *
 * Recomputes each TPU line's setup fee (qty < 25 ? $15 : $0) independently
 * of whatever DPO stored, so it stays correct even if DPO's cart properties
 * don't expose the patch/setup split. All arithmetic is done in integer
 * cents to avoid floating-point drift, which matters here because the
 * adjustment lines are added/removed automatically on every cart change.
 *
 * PERFORMANCE: theme.js's own Cart._rerender() already re-fetches this
 * section's fresh markup via Shopify's Section Rendering API and swaps it
 * in — on the cart page too, not just the mini-cart drawer — every time the
 * theme-documented cart:refresh event fires. An earlier version of this
 * file didn't trust that and did its own competing section-swap plus a
 * location.reload() fallback; running two independent fetch-and-replace
 * passes against the same DOM raced each other and left quantity buttons
 * reading stale data-quantity attributes from whichever pass lost. Firing
 * cart:refresh and letting the theme do it — same as this file already did
 * for the mini-cart — is both simpler and correct. The initial paint
 * additionally skips a network round trip entirely by reading the cart
 * snapshot main-cart.liquid embeds inline (see tpu-cart-snapshot).
 *
 * KNOWN LIMITATION: DPO pools quantity (and therefore tier + setup-fee
 * waiver) across cart lines that share identical option values — see the
 * Itoris support thread. That bug affects the *patch subtotal* this script
 * reconstructs whenever multiple lines share the same size/finish, so the
 * adjustment amount inherits that inaccuracy until Itoris resolves it. It
 * is correct for the single-design case and for designs with distinct
 * option values.
 */
(function () {
  var PRODUCT_HANDLE = 'tpu-premium-patches';
  var ADJUSTMENT_HANDLE = 'order-minimum-adjustment';
  var ORDER_MINIMUM_CENTS = 1000; // $10.00
  var SETUP_FEE_CENTS = 1500; // $15.00
  var SETUP_FEE_THRESHOLD = 25;
  var DENOMINATIONS_CENTS = [500, 100, 25, 5, 1]; // must match the adjustment product's variants
  var ADJUSTMENT_PROPERTY = '_order_minimum_adjustment';

  var evaluating = false;
  var queued = false;
  var variantMapPromise = null;

  function checkoutButtons() {
    return Array.prototype.slice.call(document.querySelectorAll('button[name="checkout"]'));
  }

  function setCheckoutEnabled(enabled) {
    checkoutButtons().forEach(function (btn) {
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', String(!enabled));
    });
  }

  // The exact cart state main-cart.liquid was rendered with, read with zero
  // network cost so the first correction pass can run before evaluate()'s
  // fetch('/cart.js') even resolves.
  function readInlineCartSnapshot() {
    var el = document.getElementById('tpu-cart-snapshot');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function cartPageSection() {
    return document.querySelector('[data-section-type="cart"]');
  }

  // Theme-documented hook (see assets/custom.js) for telling the cart page
  // table and the mini-cart drawer to re-fetch their own fresh markup via
  // theme.js's Cart._rerender() — see the file header for why this file
  // doesn't also do its own competing section fetch.
  function refreshCartUI() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (freshCart) {
        lastCart = freshCart;
        applyLineCorrection(freshCart);
        updateCartTotal(freshCart);
      })
      .catch(function (err) { console.error('[tpu-order-minimum]', err); });
  }

  // Writes only when the text actually differs — this is what lets the
  // MutationObserver below re-apply corrections on every re-render without
  // its own writes re-triggering it in an infinite loop.
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  // The same cart line renders in up to two places at once — the cart page
  // table row (tr.line-item) and the mini-cart drawer row — each with its
  // own [data-line] marker. Iterate every rendered row and map it back to
  // its cart item via its own marker, instead of document-first-match
  // lookup, so no rendered copy of a line is ever skipped.
  function forEachRenderedRow(cart, fn) {
    var rows = document.querySelectorAll('tr.line-item, .mini-cart__line-item');
    Array.prototype.forEach.call(rows, function (row) {
      var marker = row.querySelector('[data-line]');
      if (!marker) return;
      var idx = parseInt(marker.getAttribute('data-line'), 10);
      var item = cart.items[idx - 1];
      if (item) fn(row, item);
    });
  }

  function ensureSubLine(row, className, afterEl) {
    var el = row.querySelector('.' + className);
    if (!el) {
      el = document.createElement('div');
      el.className = className;
      el.style.cssText = 'font-size:0.85em;color:#666;margin-top:2px;';
      afterEl.parentNode.insertBefore(el, afterEl.nextSibling);
    }
    return el;
  }

  // Option A breakdown: the line's visible price is the raw patch ("design")
  // cost excluding setup, with separate labeled lines below for the $15
  // setup fee (hidden when waived at 25+) and the order-minimum adjustment
  // (hidden when the cart meets the $10 minimum on its own). The Total
  // column carries patch+setup+adjustment so the table sums to the real
  // charged total.
  function writeLinePrices(row, patchUnitCents, setupCents, adjustmentCents, lineTotalCents) {
    var patchDisplay = 'Price per design: $' + (patchUnitCents / 100).toFixed(2);
    var totalDisplay = '$' + (lineTotalCents / 100).toFixed(2);

    setText(row.querySelector('.line-item__price-list .line-item__price'), patchDisplay);
    setText(row.querySelector('.line-item__line-price span'), totalDisplay);
    setText(row.querySelector('.mini-cart__price-list .price'), totalDisplay);

    var list = row.querySelector('.line-item__price-list') || row.querySelector('.mini-cart__price-list');
    if (!list) return;

    var setupLine = ensureSubLine(row, 'tpu-setup-line', list);
    setText(setupLine, 'Setup fee: $15.00');
    var setupDisplay = setupCents > 0 ? '' : 'none';
    if (setupLine.style.display !== setupDisplay) setupLine.style.display = setupDisplay;

    var adjLine = ensureSubLine(row, 'tpu-adjustment-line', setupLine);
    setText(adjLine, 'Order minimum adjustment: $' + (adjustmentCents / 100).toFixed(2));
    var adjDisplay = adjustmentCents > 0 ? '' : 'none';
    if (adjLine.style.display !== adjDisplay) adjLine.style.display = adjDisplay;
  }

  // Per-line display correction is only meaningful — and only unambiguous —
  // when there's exactly one TPU design in the cart. With multiple designs
  // the combined-cart floor (per the doc, confirmed as the intended
  // behavior) means the $10 minimum may be met collectively with no
  // adjustment attributable to any single line; the real total already
  // reflects that correctly via updateCartTotal, so per-line prices in that
  // case show DPO's own raw numbers, not a floored guess.
  //
  // Every rendered copy of every TPU line gets an explicit write every time
  // this runs — either the floored numbers (single-design, under $10) or
  // its own raw numbers (everything else) — never left untouched, so a
  // correction never goes stale after the cart composition changes.
  function applyLineCorrection(cart) {
    // The real adjustment charged (hidden lines), attributed for display to
    // the first TPU line — it belongs to the order, not any one design.
    var adjustmentCents = 0;
    var firstTpuKey = null;
    cart.items.forEach(function (i) {
      if (i.handle === ADJUSTMENT_HANDLE) adjustmentCents += i.line_price;
      if (!firstTpuKey && i.handle === PRODUCT_HANDLE) firstTpuKey = i.key;
    });

    forEachRenderedRow(cart, function (row, item) {
      if (item.handle !== PRODUCT_HANDLE) return;

      var setupCents = item.quantity < SETUP_FEE_THRESHOLD ? SETUP_FEE_CENTS : 0;
      // item.line_price is Shopify's own total for the whole line (qty
      // already multiplied in) — divide back down for the per-piece display,
      // same fix as tpu-pricing-tiers.js's product-page correction.
      var patchLineCents = item.line_price - setupCents;
      var patchUnitCents = Math.round(patchLineCents / item.quantity);
      var adjCents = item.key === firstTpuKey ? adjustmentCents : 0;
      writeLinePrices(row, patchUnitCents, setupCents, adjCents, patchLineCents + setupCents + adjCents);
    });
  }

  function updateCartTotal(cart) {
    // A $0 total while the cart still has items is never real — it's a
    // sign this snapshot was read mid-mutation. Skip the write rather than
    // flash an obviously-wrong number; the next correction pass (observer
    // or evaluate()) runs against a settled cart shortly after.
    if (cart.total_price === 0 && cart.items && cart.items.length > 0) return;
    var display = '$' + (cart.total_price / 100).toFixed(2) + ' USD';
    document.querySelectorAll('[data-cart-total]').forEach(function (el) {
      setText(el, display);
    });
  }

  // Uploaded-image properties render hidden (CSS in main-cart/mini-cart
  // Liquid) because Upload Kit shows the raw URL for a few seconds before
  // swapping in a thumbnail. Reveal each one once its img exists, with a
  // rolling per-element 8s failsafe: Upload Kit only converts URLs on its
  // own page load, so rows re-rendered later (after a removal, quantity
  // change, etc.) may never get a thumbnail — those reveal as raw text
  // rather than staying hidden forever. Runs from the observer and from a
  // slow interval so it fires even when nothing else mutates.
  function revealUploadThumbs() {
    var now = Date.now();
    document.querySelectorAll('.tpu-upload-property').forEach(function (li) {
      if (li.classList.contains('has-thumb')) return; // never re-hide once shown
      if (li.querySelector('img')) { li.classList.add('has-thumb'); return; }
      if (!li.dataset.tpuSeen) li.dataset.tpuSeen = String(now);
      else if (now - Number(li.dataset.tpuSeen) > 8000) li.classList.add('has-thumb');
    });
  }
  setInterval(revealUploadThumbs, 2000);

  // Re-apply corrections whenever the cart/mini-cart markup re-renders —
  // the theme's drawer refresh, our own section swap, whatever. Scoped to
  // just the cart containers (not the whole document) since that's all
  // that's ever relevant here. setText's no-op-on-equal guard keeps our own
  // writes from re-triggering the observer in a loop.
  var lastCart = null;
  var observerTimer = null;
  var lastRun = 0;

  function runCorrections() {
    if (!lastCart) return;
    applyLineCorrection(lastCart);
    updateCartTotal(lastCart);
    revealUploadThumbs();
  }

  // THROTTLE, not debounce: Upload Kit mutates the DOM continuously for
  // several seconds while converting URL text to thumbnails — a debounce
  // (reset on every mutation) postponed all price corrections until it
  // finished. This runs immediately, then at most every 150ms during
  // sustained mutation storms, with a trailing run to catch the final state.
  var observer = new MutationObserver(function () {
    if (!lastCart) return;
    var now = Date.now();
    if (now - lastRun > 150) {
      lastRun = now;
      runCorrections();
    } else if (!observerTimer) {
      observerTimer = setTimeout(function () {
        observerTimer = null;
        lastRun = Date.now();
        runCorrections();
      }, 150);
    }
  });

  // Watches document.body, not a specific cart/mini-cart node reference.
  // An earlier version observed cartPageSection()/.mini-cart directly, which
  // seemed more efficient, but the theme's own cart re-render can replace
  // those exact nodes wholesale (not just mutate their children) — once
  // that happens the observer is left watching a detached node and goes
  // silently blind until something else happens to call startObserver()
  // again. That produced real, hard-to-reproduce bugs: a correction applied
  // once, then silently reverted to raw Shopify markup on the next
  // re-render with no further correction ever reapplied. document.body is
  // never replaced, so this can't happen.
  function startObserver() {
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function breakdown(cents) {
    var parts = [];
    DENOMINATIONS_CENTS.forEach(function (d) {
      var count = Math.floor(cents / d);
      if (count > 0) {
        parts.push({ cents: d, quantity: count });
        cents -= count * d;
      }
    });
    return parts;
  }

  function getVariantMap() {
    if (!variantMapPromise) {
      variantMapPromise = fetch('/products/' + ADJUSTMENT_HANDLE + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('Order Minimum Adjustment product not found — has it been imported and published?');
          return r.json();
        })
        .then(function (product) {
          var map = {};
          product.variants.forEach(function (v) { map[v.price] = v.id; });
          return map;
        });
    }
    return variantMapPromise;
  }

  function removeLines(lines) {
    if (!lines.length) return Promise.resolve();
    var updates = {};
    lines.forEach(function (line) { updates[line.key] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }

  function addAdjustment(cents) {
    return getVariantMap().then(function (map) {
      var parts = breakdown(cents);
      var items = parts.map(function (p) {
        var variantId = map[p.cents];
        if (!variantId) throw new Error('No Order Minimum Adjustment variant priced at ' + (p.cents / 100).toFixed(2));
        var props = {};
        props[ADJUSTMENT_PROPERTY] = 'true';
        return { id: variantId, quantity: p.quantity, properties: props };
      });
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });
    });
  }

  function evaluate() {
    if (evaluating) {
      queued = true;
      return;
    }
    evaluating = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hasTpu = cart.items.some(function (i) { return i.handle === PRODUCT_HANDLE; });

        var patchSubtotalCents = 0;
        cart.items.forEach(function (item) {
          if (item.handle !== PRODUCT_HANDLE) return;
          var setupFeeCents = item.quantity < SETUP_FEE_THRESHOLD ? SETUP_FEE_CENTS : 0;
          patchSubtotalCents += item.line_price - setupFeeCents;
        });

        var adjustmentLines = cart.items.filter(function (i) { return i.handle === ADJUSTMENT_HANDLE; });
        var currentAdjustmentCents = adjustmentLines.reduce(function (sum, i) { return sum + i.line_price; }, 0);

        var neededCents = hasTpu ? Math.max(0, ORDER_MINIMUM_CENTS - patchSubtotalCents) : 0;

        // Applied on every check, mutation or not — a fresh page load or a
        // theme-driven re-render always renders raw, uncorrected values.
        // Safe to use this cart snapshot here since nothing's been mutated
        // yet in this cycle.
        lastCart = cart;
        applyLineCorrection(cart);
        updateCartTotal(cart);
        revealUploadThumbs();

        if (neededCents === currentAdjustmentCents) {
          return null;
        }

        // Only touch checkout's enabled state for the actual mutation window —
        // not on every read-only check, so carts without TPU items (or already
        // correctly adjusted ones) never see the button flicker.
        setCheckoutEnabled(false);
        var chain = removeLines(adjustmentLines);
        if (neededCents > 0) {
          chain = chain.then(function () { return addAdjustment(neededCents); });
        }
        return chain.then(function () {
          setCheckoutEnabled(true);
          return refreshCartUI();
        });
      })
      .catch(function (err) {
        // Fail open: never leave checkout blocked because of a bug here.
        console.error('[tpu-order-minimum]', err);
        setCheckoutEnabled(true);
      })
      .then(function () {
        evaluating = false;
        startObserver(); // targets may have changed after a section swap
        if (queued) {
          queued = false;
          evaluate();
        }
      });
  }

  var CART_ENDPOINTS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  function isCartUrl(url) {
    return typeof url === 'string' && CART_ENDPOINTS.some(function (ep) {
      return url.indexOf(ep) !== -1;
    });
  }

  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0] && arguments[0].url ? arguments[0].url : arguments[0];
    var result = originalFetch.apply(this, arguments);
    if (isCartUrl(url)) {
      result.then(function (res) { setTimeout(evaluate, 50); return res; });
    }
    return result;
  };

  // The theme's cart page quantity controls (jQuery) go through
  // XMLHttpRequest, not fetch — without this they'd never trigger a
  // re-evaluation and the adjustment/labels would only fix on reload.
  var originalXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartUrl(url)) {
      this.addEventListener('loadend', function () { setTimeout(evaluate, 50); });
    }
    return originalXhrOpen.apply(this, arguments);
  };

  document.addEventListener('DOMContentLoaded', function () {
    // Instant first paint: correct against the exact cart state this page
    // was rendered with (zero network cost) before evaluate()'s own
    // fetch('/cart.js') round trip even resolves.
    var snapshot = readInlineCartSnapshot();
    if (snapshot) {
      lastCart = snapshot;
      applyLineCorrection(snapshot);
      updateCartTotal(snapshot);
      revealUploadThumbs();
    }
    startObserver();
    evaluate();
  });
  document.addEventListener('cart:updated', evaluate);
  document.addEventListener('cart:refresh', evaluate);
  document.addEventListener('product:added', evaluate);
  // Back/forward navigation restores the page from bfcache without firing
  // DOMContentLoaded — the cart may have changed since this page was cached.
  window.addEventListener('pageshow', function (e) { if (e.persisted) evaluate(); });
})();
